<script setup lang="ts">
import type { LogicalFilter } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { cloneDeep } from 'lodash-es';
import { useEmitAsProps } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isFieldFilter, isLogicalFilter, isLogicalFilterAnd } from '#layers/data-model/public';
import { useFilterGate } from '#layers/filters/composables/use-filter-gate';
import { getFilterList, useLogicalFilter } from '#layers/filters/composables/use-logical-filter';
import { useRelatedItemMatch } from '#layers/filters/composables/use-related-item-match';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

export type ClearFilterDisplayStyle = 'none' | 'button' | 'link';

const props = withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	hideGroupActions?: boolean;
	hideOuterGroup?: boolean;
	disabled?: boolean;
	clearFilterDisplayStyle?: ClearFilterDisplayStyle;
	readonly?: boolean;
	ui?: Partial<typeof filterLogicalGroup.slots>;
	enableVariables?: boolean;
	includeGroupAction?: boolean;
}>(), {
	hideGroupActions: true,
	clearFilterDisplayStyle: 'none',
});

const emits = defineEmits<{
	remove: [];
	duplicate: [];
}>();

const filterLogicalGroup = tv({
	slots: {
		root: 'flex flex-col gap-2',
		operatorButton: 'px-2 uppercase',
		operatorText: 'inline-flex flex-1 items-center px-2 text-xs font-medium whitespace-nowrap text-dimmed',
		body: 'flex items-start gap-2',
		list: 'flex flex-1 flex-col gap-2',
		addRow: 'flex items-center gap-2',
		addButton: 'w-full',
		clearButton: 'text-muted',
	},
	variants: {
		showOuterGroup: {
			true: {
				list: 'ml-3 border-l pl-3',
			},
		},
	},
});

const filter = defineModel<LogicalFilter>({ required: true });
const actionEmits = useEmitAsProps(emits);
const ui = computed(() => filterLogicalGroup({ showOuterGroup: !props.hideOuterGroup }));

const { t } = useI18n();
const operator = computed(() => isLogicalFilterAnd(filter.value) ? '_and' : '_or');
const operatorLabel = computed(() => t(`filters.operators.${operator.value}`));

const filterList = computed({
	get() {
		return cloneDeep(getFilterList(filter.value));
	},
	set(value) {
		filter.value = { [operator.value]: value } as LogicalFilter;
	},
});

const { findPrimitiveFieldById, findSingleRelationFieldById } = injectFilterSchemaContext();
const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();
const { isRelatedItemMatch } = useRelatedItemMatch();

const gate = useFilterGate();

const { canToggleOperator, handleAdd, handleGroupAdd, handleAddCondition, handleUpdate, handleRemove, handleDuplicate, handleTurnIntoGroup, handleToggleOperator, handleClearFilter } = useLogicalFilter(filter, {
	findPrimitiveFieldById,
	findSingleRelationFieldById,
	allowsClause: gate.allowsClause,
	supportedOperatorsFor,
	defaultOperatorFor,
});
</script>

<template>
	<div :class="ui.root({ class: props.ui?.root })">
		<FilterHandleGroup v-if="!hideOuterGroup">
			<DButton
				:class="ui.operatorButton({ class: props.ui?.operatorButton })"
				variant="link"
				color="primary"
				size="sm"
				:disabled="disabled || !canToggleOperator()"
				@click="handleToggleOperator"
			>
				{{ operatorLabel }}
			</DButton>
			<span :class="ui.operatorText({ class: props.ui?.operatorText })">{{ $t(`filters.${operator}_text`) }}</span>
			<FilterActions v-if="hideGroupActions" v-bind="actionEmits" :disabled />
		</FilterHandleGroup>
		<div :class="ui.body({ class: props.ui?.body })">
			<ul :class="ui.list({ class: props.ui?.list })">
				<li v-for="(subFilter, index) in filterList" :key="index">
					<FilterRelatedItem
						v-if="isRelatedItemMatch(subFilter, collection)"
						hide-group-actions
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
						@turn-into-group="handleTurnIntoGroup(index)"
					/>
					<FilterSimple
						v-else-if="isFieldFilter(subFilter)"
						hide-group-actions
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						:enable-variables
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
						@turn-into-group="handleTurnIntoGroup(index)"
					/>
					<FilterLogicalGroup
						v-else-if="isLogicalFilter(subFilter)"
						:model-value="subFilter"
						:collection
						:disabled
						:readonly
						:enable-variables
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@duplicate="handleDuplicate(index)"
					/>
				</li>
				<li>
					<div :class="ui.addRow({ class: props.ui?.addRow })">
						<FilterAdd
							v-if="!readonly"
							:class="ui.addButton({ class: props.ui?.addButton })"
							:collection
							:disabled
							:include-group-action
							@add-filter="handleAdd"
							@add-condition="handleAddCondition"
							@add-group="handleGroupAdd"
						>
							<template #default>
								<slot name="filter-add">
									<DButton
										color="neutral"
										variant="ghost"
										icon="lucide:plus"
										:aria-label="$t('filters.filter')"
										:label="$t('filters.add_filter')"
										size="sm"
										:disabled
									/>
								</slot>
							</template>
						</FilterAdd>
						<DButton
							v-if="clearFilterDisplayStyle === 'button' && filterList.length > 0"
							:label="t('filters.clear_filter')"
							color="neutral"
							variant="ghost"
							:class="ui.clearButton({ class: props.ui?.clearButton })"
							icon="lucide:x"
							size="sm"
							:disabled
							:readonly
							@click="handleClearFilter"
						/>
					</div>
				</li>
			</ul>
		</div>
	</div>
</template>
